<template>
    <div
        class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
    >
        <p
            v-if="error"
            class="bg-white px-5 py-6 text-sm text-rose-500 dark:bg-gray-800"
        >
            Não foi possível carregar o retrato do seller.
        </p>

        <div
            v-else-if="loading"
            class="grid grid-cols-1 gap-px bg-gray-200 dark:bg-gray-700"
            :class="gridColsClass(skeletonCount)"
        >
            <div
                v-for="n in skeletonCount"
                :key="n"
                class="space-y-2 bg-white px-4 py-3 dark:bg-gray-800"
            >
                <div
                    class="h-2 w-24 rounded-sm bg-gray-100 dark:bg-gray-700"
                ></div>
                <div
                    class="h-4 w-20 rounded-sm bg-gray-100 dark:bg-gray-700"
                ></div>
                <div
                    class="h-7 w-full rounded-sm bg-gray-100 dark:bg-gray-700"
                ></div>
            </div>
        </div>

        <div
            v-else-if="cards.length"
            class="grid grid-cols-1 gap-px bg-gray-200 dark:bg-gray-700"
            :class="gridColsClass(cards.length)"
        >
            <article
                v-for="card in cards"
                :key="card.key"
                role="button"
                tabindex="0"
                :aria-label="`${card.label}: ${card.value}. Abrir detalhamento`"
                class="group relative flex cursor-pointer flex-col bg-white text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 dark:bg-gray-800 dark:hover:bg-gray-700/60"
                :class="cardWash(card.status)"
                @click="$emit('open-detail', card.key)"
                @keydown.enter.prevent="$emit('open-detail', card.key)"
                @keydown.space.prevent="$emit('open-detail', card.key)"
            >
                <!-- Só o bloco de texto tem padding: a sparkline sangra até as
                     laterais e o rodapé do card. -->
                <div class="px-4 pb-2 pt-3">
                    <div class="flex items-center justify-between gap-2">
                        <h3
                            class="truncate text-[10px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                        >
                            {{ card.label }}
                        </h3>
                        <span
                            v-if="card.status !== 'neutral'"
                            class="shrink-0 rounded-full border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em]"
                            :class="statusChip(card.status)"
                        >
                            {{ statusLabel(card.status) }}
                        </span>
                    </div>

                    <p
                        class="mt-1.5 truncate font-mono text-base font-bold tabular-nums tracking-tight text-gray-900 dark:text-white"
                        :title="card.value"
                    >
                        {{ card.value }}
                    </p>

                    <p
                        class="mt-1 line-clamp-2 min-h-[2rem] text-[10px] leading-relaxed text-gray-500 dark:text-gray-400"
                        :title="card.secondary"
                    >
                        {{ card.secondary }}
                    </p>
                </div>

                <!-- Sparkline: fade curto ao trocar o período (sem animação
                     contínua). Cards de referência não têm série. -->
                <transition name="portrait-fade" mode="out-in">
                    <svg
                        v-if="card.series && card.series.length"
                        :key="`${card.key}-${portrait.period_days}`"
                        class="mt-auto block h-8 w-full"
                        :class="toneClass(card)"
                        viewBox="0 0 100 26"
                        preserveAspectRatio="none"
                        role="img"
                        :aria-label="`Evolução diária de ${card.label} no período`"
                        @mouseleave="clearHover(card.key)"
                    >
                        <path
                            :d="areaPath(card.series)"
                            class="fill-current opacity-10"
                        />
                        <path
                            :d="linePath(card.series)"
                            fill="none"
                            class="stroke-current"
                            stroke-width="1.4"
                            stroke-linejoin="round"
                            stroke-linecap="round"
                            vector-effect="non-scaling-stroke"
                        />
                        <circle
                            v-if="hovered[card.key] != null"
                            :cx="pointX(hovered[card.key], card.series.length)"
                            :cy="
                                pointY(
                                    card.series[hovered[card.key]].value,
                                    card.series,
                                )
                            "
                            r="1.6"
                            class="fill-current"
                        />
                        <rect
                            v-for="(point, index) in card.series"
                            :key="point.date"
                            :x="hitX(index, card.series.length)"
                            y="0"
                            :width="hitWidth(card.series.length)"
                            height="26"
                            fill="transparent"
                            @mouseenter="setHover(card.key, index)"
                            @touchstart.passive="setHover(card.key, index)"
                        />
                    </svg>
                    <p
                        v-else
                        :key="`${card.key}-ref`"
                        class="mt-auto flex h-8 items-center px-4 pb-1 font-mono text-[9px] uppercase tracking-[0.1em] text-gray-300 dark:text-gray-600"
                    >
                        {{ card.reference_note || "sem série diária" }}
                    </p>
                </transition>

                <!-- Tooltip flutuante sobre o canto do gráfico: a data e o
                     valor do dia sob o cursor, sem linha reservada no layout. -->
                <p
                    v-if="
                        hovered[card.key] != null &&
                        card.series[hovered[card.key]]
                    "
                    class="pointer-events-none absolute bottom-9 right-2 rounded bg-gray-900/90 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-white shadow-sm dark:bg-gray-100/90 dark:text-gray-900"
                    aria-live="polite"
                >
                    {{ dayLabel(card.series[hovered[card.key]].date) }} ·
                    {{ pointLabel(card, card.series[hovered[card.key]]) }}
                </p>
            </article>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        // O fetch vive na página (AuditUserMetrics): o mesmo retrato alimenta
        // o farol e as três seções, sem repetir a request por seção.
        portrait: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        error: { type: Boolean, default: false },
        // Quais cards do retrato esta instância mostra, na ordem dada.
        keys: { type: Array, required: true },
    },

    emits: ["open-detail"],

    data() {
        return {
            hovered: {},
        };
    },

    computed: {
        cards() {
            const byKey = new Map(
                (this.portrait?.cards || []).map((card) => [card.key, card]),
            );

            return this.keys.map((key) => byKey.get(key)).filter(Boolean);
        },

        skeletonCount() {
            return this.keys.length;
        },
    },

    watch: {
        portrait() {
            this.hovered = {};
        },
    },

    methods: {
        gridColsClass(count) {
            if (count <= 2) {
                return "sm:grid-cols-2";
            }

            if (count === 3) {
                return "sm:grid-cols-2 lg:grid-cols-3";
            }

            return "sm:grid-cols-2 lg:grid-cols-4";
        },

        setHover(key, index) {
            this.hovered = { ...this.hovered, [key]: index };
        },

        clearHover(key) {
            const next = { ...this.hovered };
            delete next[key];
            this.hovered = next;
        },

        peak(series) {
            return Math.max(...series.map((point) => point.value), 1);
        },

        pointX(index, length) {
            return length > 1 ? (index / (length - 1)) * 100 : 50;
        },

        pointY(value, series) {
            // 2px de folga no topo, base em y=25 (1px do rodapé): a linha da
            // série zerada encosta no chão do card, e a área fecha na borda.
            return 25 - (Math.max(0, value) / this.peak(series)) * 23;
        },

        linePath(series) {
            return series
                .map((point, index) => {
                    const x = this.pointX(index, series.length).toFixed(2);
                    const y = this.pointY(point.value, series).toFixed(2);

                    return `${index === 0 ? "M" : "L"}${x},${y}`;
                })
                .join(" ");
        },

        areaPath(series) {
            return `${this.linePath(series)} L100,26 L0,26 Z`;
        },

        hitWidth(length) {
            return 100 / Math.max(length, 1);
        },

        hitX(index, length) {
            return this.pointX(index, length) - this.hitWidth(length) / 2;
        },

        dayLabel(date) {
            // Série em ISO (Y-m-d); new Date() de string ISO pura é UTC e
            // voltaria um dia no fuso do Brasil.
            const [year, month, day] = date.split("-");

            return `${day}/${month}/${year}`;
        },

        pointLabel(card, point) {
            if (point.masked) {
                return "valor restrito";
            }

            if (card.kind === "currency") {
                return (point.value / 100).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                });
            }

            if (card.kind === "percent") {
                return `${point.value}%`;
            }

            return `${point.value}`;
        },

        toneClass(card) {
            // Cor por semântica; o estado nunca fica só na cor (há chip com
            // texto e o valor/contexto em texto).
            if (card.status === "critical") {
                return "text-rose-500 dark:text-rose-400";
            }

            return (
                {
                    primary: "text-indigo-500 dark:text-indigo-400",
                    amber: "text-amber-500 dark:text-amber-400",
                    rose: "text-rose-500 dark:text-rose-400",
                    emerald: "text-emerald-500 dark:text-emerald-400",
                }[card.tone] ?? "text-indigo-500 dark:text-indigo-400"
            );
        },

        statusLabel(status) {
            return {
                ok: "ok",
                attention: "atenção",
                critical: "alerta",
                neutral: "",
            }[status];
        },

        statusChip(status) {
            return {
                ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                attention:
                    "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
                critical:
                    "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400",
            }[status];
        },

        cardWash(status) {
            return (
                {
                    attention: "!bg-amber-50/70 dark:!bg-amber-500/[0.04]",
                    critical: "!bg-rose-50/70 dark:!bg-rose-500/[0.05]",
                }[status] ?? ""
            );
        },
    },
};
</script>

<style scoped>
.portrait-fade-enter-active,
.portrait-fade-leave-active {
    transition: opacity 0.15s ease;
}

.portrait-fade-enter-from,
.portrait-fade-leave-to {
    opacity: 0;
}
</style>
